import React, { useState } from 'react';
import { Link, Outlet, useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard,
  FlaskConical,
  Users,
  ClipboardList,
  Settings,
  ShieldCheck,
  Bell,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Home,
  Search,
  LogOut,
  User,
  BarChart3,
  Globe,
  Building2,
  Target,
  Activity,
  Calendar,
  AlertTriangle,
  DollarSign,
  Truck,
  FileText,
  PieChart,
} from 'lucide-react';
import { fetchStudies, fetchNotifications } from '@/api/endpoints';
import { useAuth } from '@/contexts/AuthContext';
import type { Notification } from '@/types';

// ─── Sidebar nav definition ─────────────────────────────────────────────────

interface NavItem {
  label: string;
  to: string;
  icon: React.ElementType;
  children?: NavItem[];
}

const mainNav: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { label: 'My Work', to: '/my-work', icon: ClipboardList },
  { label: 'Studies', to: '/studies', icon: FlaskConical },
  { label: 'Investigators', to: '/investigators', icon: Users },
  { label: 'Notifications', to: '/notifications', icon: Bell },
  { label: 'AI Governance', to: '/ai-governance', icon: ShieldCheck },
  { label: 'Audit Log', to: '/audit-log', icon: ClipboardList },
  { label: 'Settings', to: '/settings', icon: Settings },
];

// ─── Sidebar component ─────────────────────────────────────────────────────

function Sidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['main']),
  );

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-30 flex h-screen flex-col border-r border-gray-200 bg-white transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo / brand */}
      <div className="flex h-14 items-center justify-between border-b border-gray-200 px-4">
        {!collapsed && (
          <span className="text-lg font-bold text-indigo-600">Wave CTMS</span>
        )}
        <button
          onClick={onToggle}
          className="rounded p-1 text-gray-500 hover:bg-gray-100"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <Menu size={20} /> : <X size={20} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="space-y-1">
          {mainNav.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 [&.active]:bg-indigo-50 [&.active]:text-indigo-700"
                activeProps={{ className: 'active bg-indigo-50 text-indigo-700' }}
                title={collapsed ? item.label : undefined}
              >
                <item.icon size={18} className="shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="border-t border-gray-200 p-3 text-xs text-gray-400">
          Wave CTMS v1.0
        </div>
      )}
    </aside>
  );
}

// ─── Breadcrumbs ────────────────────────────────────────────────────────────

function Breadcrumbs() {
  const matches = useMatches();

  const crumbs = matches
    .filter((m) => m.pathname !== '/')
    .map((m) => ({
      label: formatBreadcrumb(m.pathname),
      to: m.pathname,
    }));

  if (crumbs.length === 0) return null;

  return (
    <nav className="flex items-center gap-1 text-sm text-gray-500" aria-label="Breadcrumb">
      <Link to="/dashboard" className="hover:text-indigo-600">
        <Home size={14} />
      </Link>
      {crumbs.map((crumb, i) => (
        <React.Fragment key={crumb.to}>
          <ChevronRight size={14} className="text-gray-300" />
          {i === crumbs.length - 1 ? (
            <span className="font-medium text-gray-900">{crumb.label}</span>
          ) : (
            <Link to={crumb.to} className="hover:text-indigo-600">
              {crumb.label}
            </Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function formatBreadcrumb(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? '';
  return last
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Top bar ────────────────────────────────────────────────────────────────

function TopBar() {
  const { user, logout } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Derive initials and display name from the authenticated user
  const displayName = user?.name ?? 'User';
  const initials = displayName
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // const { data: notifications = [] } = useQuery<Notification[]>({
  //   queryKey: ['notifications'],
  //   queryFn: fetchNotifications,
  //   refetchInterval: 30_000,
  // });

  const { data } = useQuery<Notification[]>({
    queryKey: ['notifications'],
    queryFn: fetchNotifications,
    refetchInterval: 30_000,
  });


  console.log(data,"DATA")
  // Defensive: never let an unexpected response shape crash the app shell.
  const notifications = Array.isArray(data) ? data : [];
  const unreadCount = notifications.filter((n) => !n.read).length;
  // const unreadCount = (notifications || []).filter((n) => !n.read).length;

   console.log(notifications,"notifications")

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-6">
      <Breadcrumbs />

      <div className="flex items-center gap-4">
        {/* Search */}
        <div className="relative hidden md:block">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search studies, sites..."
            className="w-64 rounded-md border border-gray-300 py-1.5 pl-9 pr-3 text-sm outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400"
          />
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowUserMenu(false);
            }}
            className="relative rounded-md p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Notifications"
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-1 w-80 rounded-lg border border-gray-200 bg-white shadow-lg">
              <div className="border-b border-gray-100 px-4 py-2 text-sm font-semibold text-gray-700">
                Notifications
              </div>
              <ul className="max-h-72 overflow-y-auto">
                {notifications.length === 0 && (
                  <li className="px-4 py-6 text-center text-sm text-gray-400">
                    No notifications
                  </li>
                )}
                {notifications?.slice(0, 8)?.map((n) => (
                  <li
                    key={n.id}
                    className={`border-b border-gray-50 px-4 py-2 text-sm ${
                      n.read ? 'text-gray-500' : 'bg-indigo-50/50 text-gray-800'
                    }`}
                  >
                    <p className="font-medium">{n.title}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(n.createdAt).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
            }}
            className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">
              {initials}
            </div>
            <span className="hidden md:inline">{displayName}</span>
            <ChevronDown size={14} />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
              <Link
                to="/settings"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setShowUserMenu(false)}
              >
                <User size={14} /> Profile
              </Link>
              <Link
                to="/settings"
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => setShowUserMenu(false)}
              >
                <Settings size={14} /> Settings
              </Link>
              <hr className="my-1 border-gray-100" />
              <button
                onClick={() => {
                  setShowUserMenu(false);
                  logout();
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
              >
                <LogOut size={14} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── Main layout ────────────────────────────────────────────────────────────

export default function AppLayout() {
  const { isAuthenticated } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // If not authenticated, render only the Outlet (login page) without the shell
  if (!isAuthenticated) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div
        className={`transition-all duration-200 ${
          sidebarCollapsed ? 'ml-16' : 'ml-60'
        }`}
      >
        <TopBar />
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
